import styled from 'styled-components';

export const Base = styled.div`
	display: flex;
	align-items: center;
	height: 36px;
	flex-shrink: 0;
	min-width: 0;
	width: 100%;
	position: relative;

	// The bar's hairline is drawn underneath the tabs rather than as a border, so
	// the active tab's own fill covers the 1px beneath it and the line reads as
	// notched. Keeping it inside the tab's box means the strip can clip freely.
	&::after {
		content: '';
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		height: 1px;
		background: ${({ theme }) => theme.gray[200]};
		z-index: 0;
		pointer-events: none;
	}
`;

// Tabs stop shrinking at their min-width, so without this they would spill past
// the strip and over the add button beside it.
export const TabBar = styled.div`
	display: flex;
	align-items: center;
	gap: 0;
	height: 100%;
	min-width: 0;
	flex: 0 1 auto;
	overflow: hidden;
`;

export const TabCloseButton = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 18px;
	height: 18px;
	border-radius: 6px;
	border: none;
	background: ${({ theme }) => theme.gray[100]};
	color: ${({ theme }) => theme.gray[1000]};
	opacity: 0;
	pointer-events: none;
	transition: opacity 0.15s ease, background-color 0.15s ease;
	padding: 0;
	position: absolute;
	right: 6px;
	top: 50%;
	transform: translateY(-50%);
	z-index: 1;
	user-select: none;
	&:hover {
		background: ${({ theme }) => theme.gray[400]};
	}

	&:focus-visible {
		outline: none;
		box-shadow: 0 0 0 2px ${({ theme }) => theme.blue[500]};
	}
`;

export const TabCloseBacking = styled.div`
	position: absolute;
	right: 4px;
	top: 50%;
	transform: translateY(-50%);
	width: 20px;
	height: 20px;
	border-radius: 0px;
	background: ${({ theme }) => theme.gray[100]};
	z-index: 0;
	opacity: 0;
	transition: opacity 0.15s ease;
`;

export const AddTabButtonWrapper = styled.div`
	flex-shrink: 0;
	margin-left: 4px;
`;

export const TabIcon = styled.span`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
`;

export const Tab = styled.button<{
	$active?: boolean;
	$isDragging?: boolean;
}>`
	display: flex;
	align-items: center;
	gap: 6px;
	// symmetric padding: the close button floats over the label end on hover
	// instead of holding a slot
	padding: 0 12px;
	height: 100%;
	border: none;
	background: ${({ $active, theme }) => ($active ? theme.gray[100] : 'transparent')};
	color: ${({ $active, theme }) => ($active ? theme.gray[1000] : theme.gray[700])};
	font-family: 'Inter', sans-serif;
	font-size: 14px;
	font-weight: 500;
	transition: color 0.2s ease-in-out, background-color 0.2s ease-in-out, opacity 0.2s ease-in-out;
	user-select: none;
	outline: none;
	// sits above the bar's hairline, so an active tab's fill breaks the line
	position: relative;
	z-index: 1;
	white-space: nowrap;
	flex: 0 1 auto;
	min-width: 44px;
	max-width: 240px;
	overflow: hidden;
	opacity: ${({ $isDragging }) => ($isDragging ? 0.4 : 1)};

	&:focus-visible {
		outline: none;
		box-shadow: inset 0 0 0 2px ${({ theme }) => theme.blue[500]};
	}

	// the close button is hover-revealed, so keyboard focus has to reveal it too
	&:focus-within {
		${TabCloseButton} {
			opacity: 1;
			pointer-events: auto;
		}

		${TabCloseBacking} {
			opacity: 1;
		}
	}

	&:hover {
		${TabCloseButton} {
			opacity: 1;
			pointer-events: auto;
		}

		${TabCloseBacking} {
			opacity: 1;
		}

		${({ $active, theme }) =>
			!$active
				? `
			background-color: ${theme.gray[100]};
			color: ${theme.gray[1000]};
		`
				: ''}
	}
`;

export const HomeTab = styled(Tab)``;

export const TabLabel = styled.span`
	white-space: nowrap;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	flex: 1;
`;
